import React, { useEffect, useState } from 'react';
import AnimatedBackground from '../components/AnimatedBackground';
import { GYM_INFO } from '../mock';
import { MapPin, Phone, Mail, Clock, MessageCircle, Send } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: '',
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Create WhatsApp message
    const message = `Hi, my name is ${formData.name}. 
Phone: ${formData.phone}
Email: ${formData.email}
Message: ${formData.message}`;
    
    // Open WhatsApp with pre-filled message
    window.open(
      `${GYM_INFO.whatsappLink}?text=${encodeURIComponent(message)}`,
      '_blank'
    );

    // Show success message
    toast({
      title: "Redirecting to WhatsApp",
      description: "Our team will contact you shortly!",
    });

    // Reset form
    setFormData({ name: '', phone: '', email: '', message: '' });
  };

  return (
    <div className="min-h-screen bg-black pt-24 pb-16">
      <AnimatedBackground />

      <div className="container mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-5xl md:text-7xl font-black text-white mb-4 uppercase">
            Get in <span className="text-[#39FF14]">Touch</span>
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Have questions? We're here to help you start your fitness journey
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Contact Info */}
          <div>
            <h2 className="text-3xl font-black text-white mb-8 uppercase">
              Visit <span className="text-[#39FF14]">Us</span>
            </h2>

            <div className="space-y-6">
              {/* Location */}
              <div className="flex items-start gap-4 bg-gradient-to-br from-[#1a1a1a] to-black p-6 rounded-2xl border border-[#39FF14]/20">
                <div className="w-12 h-12 bg-[#39FF14]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-6 h-6 text-[#39FF14]" />
                </div>
                <div>
                  <h3 className="text-white font-bold mb-1">Location</h3>
                  <p className="text-gray-400">{GYM_INFO.location}</p>
                </div>
              </div>

              {/* Phone */}
              <div className="flex items-start gap-4 bg-gradient-to-br from-[#1a1a1a] to-black p-6 rounded-2xl border border-[#39FF14]/20">
                <div className="w-12 h-12 bg-[#39FF14]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Phone className="w-6 h-6 text-[#39FF14]" />
                </div>
                <div>
                  <h3 className="text-white font-bold mb-1">Phone</h3>
                  <a
                    href={`tel:${GYM_INFO.whatsapp}`}
                    className="text-gray-400 hover:text-[#39FF14] transition-colors"
                  >
                    {GYM_INFO.whatsapp}
                  </a>
                </div>
              </div>

              {/* WhatsApp */}
              <div className="flex items-start gap-4 bg-gradient-to-br from-[#1a1a1a] to-black p-6 rounded-2xl border border-[#39FF14]/20">
                <div className="w-12 h-12 bg-[#39FF14]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <MessageCircle className="w-6 h-6 text-[#39FF14]" />
                </div>
                <div>
                  <h3 className="text-white font-bold mb-1">WhatsApp</h3>
                  <a
                    href={`${GYM_INFO.whatsappLink}?text=Hi, I'm interested in Iron Crust Gym!`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-[#39FF14] transition-colors"
                  >
                    Chat with us instantly
                  </a>
                </div>
              </div>

              {/* Hours */}
              <div className="flex items-start gap-4 bg-gradient-to-br from-[#1a1a1a] to-black p-6 rounded-2xl border border-[#39FF14]/20">
                <div className="w-12 h-12 bg-[#39FF14]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Clock className="w-6 h-6 text-[#39FF14]" />
                </div>
                <div>
                  <h3 className="text-white font-bold mb-2">Operating Hours</h3>
                  <div className="space-y-1 text-gray-400 text-sm">
                    <p>Monday - Saturday: 6:00 AM - 11:00 PM</p>
                    <p>Sunday: 7:00 AM - 9:00 PM</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick WhatsApp CTA */}
            <div className="mt-8 bg-gradient-to-br from-[#39FF14]/10 to-transparent p-8 rounded-3xl border border-[#39FF14]/30">
              <h3 className="text-2xl font-black text-white mb-3 uppercase">
                Quick Response?
              </h3>
              <p className="text-gray-400 mb-6">
                Get instant answers on WhatsApp. We're online and ready to help!
              </p>
              <a
                href={`${GYM_INFO.whatsappLink}?text=Hi, I need information about Iron Crust Gym`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#39FF14] text-black font-bold uppercase rounded-full hover:scale-105 transition-transform"
              >
                <MessageCircle className="w-5 h-5" />
                Chat Now
              </a>
            </div>
          </div>

          {/* Contact Form */}
          <div>
            <h2 className="text-3xl font-black text-white mb-8 uppercase">
              Send a <span className="text-[#39FF14]">Message</span>
            </h2>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-white font-bold mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-6 py-4 bg-[#1a1a1a] text-white border border-[#39FF14]/20 rounded-xl focus:border-[#39FF14] focus:outline-none transition-colors"
                  placeholder="Enter your name"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-white font-bold mb-2">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-6 py-4 bg-[#1a1a1a] text-white border border-[#39FF14]/20 rounded-xl focus:border-[#39FF14] focus:outline-none transition-colors"
                  placeholder="+91 XXXXX XXXXX"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-white font-bold mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-6 py-4 bg-[#1a1a1a] text-white border border-[#39FF14]/20 rounded-xl focus:border-[#39FF14] focus:outline-none transition-colors"
                  placeholder="your@email.com"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-white font-bold mb-2">
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  className="w-full px-6 py-4 bg-[#1a1a1a] text-white border border-[#39FF14]/20 rounded-xl focus:border-[#39FF14] focus:outline-none transition-colors resize-none"
                  placeholder="Tell us what you're interested in..."
                />
              </div>

              <button
                type="submit"
                className="w-full flex items-center justify-center gap-3 px-8 py-4 bg-[#39FF14] text-black font-bold text-lg uppercase rounded-full hover:scale-105 hover:shadow-2xl hover:shadow-[#39FF14]/50 transition-all duration-300"
              >
                Send via WhatsApp
                <Send className="w-5 h-5" />
              </button>

              <p className="text-gray-500 text-sm text-center">
                This form will open WhatsApp with your message pre-filled
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
